import { useState, useRef } from 'react';
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronDown } from "lucide-react";
import { APP_LOGO } from "@shared/const";
import { Button } from "@/components/ui/button";

export default function HeaderHero() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sobreNosOpen, setSobreNosOpen] = useState(false);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path: string) => location === path;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full px-8 md:px-12 py-6 flex items-center justify-between border-b border-white/10 relative z-50">
      {/* Logo */}
      <Link href="/">
        <img 
          src="/images/logos/Fercci_AssinaturaVisual_AuroraeBranco.png"
          alt="Fercci" 
          className="h-8 w-auto cursor-pointer transition-all"
        />
      </Link>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center gap-8">
        <Link href="/" onClick={scrollToTop}>
          <span className={`text-base font-semibold transition-colors cursor-pointer ${
            isActive("/") ? "text-accent" : "text-white hover:text-accent"
          }`}>
            Início
          </span>
        </Link>
        
        <Link href="/contato" onClick={scrollToTop}>
          <span className={`text-base font-semibold transition-colors cursor-pointer ${
            isActive("/contato") ? "text-accent" : "text-white hover:text-accent"
          }`}>
            Contato
          </span>
        </Link>
        
        <Link href="/servicos" onClick={scrollToTop}>
          <span className={`text-base font-semibold transition-colors cursor-pointer ${
            isActive("/servicos") ? "text-accent" : "text-white hover:text-accent"
          }`}>
            Serviços
          </span>
        </Link>
        
        <Link href="/tecnologias" onClick={scrollToTop}>
          <span className={`text-base font-semibold transition-colors cursor-pointer ${
            isActive("/tecnologias") ? "text-accent" : "text-white hover:text-accent"
          }`}>
            Tecnologias
          </span>
        </Link>
        
        {/* Dropdown Sobre Nós */}
        <div 
          className="relative"
          onMouseEnter={() => {
            if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
            setSobreNosOpen(true);
          }}
          onMouseLeave={() => {
            dropdownTimeoutRef.current = setTimeout(() => setSobreNosOpen(false), 200);
          }}
        >
          <button className={`flex items-center gap-1 text-base font-semibold transition-colors ${
            isActive("/sobre-nos") || isActive("/nossos-eventos") || isActive("/programa-fidelidade") 
              ? "text-accent" 
              : "text-white hover:text-accent"
          }`}>
            Sobre Nós
            <ChevronDown className="h-4 w-4" />
          </button>
          
          {sobreNosOpen && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-card border rounded-md shadow-lg py-2 z-[100]">
              <Link href="/sobre-nos" onClick={scrollToTop}>
                <span className="block px-4 py-2 text-sm hover:bg-muted cursor-pointer">
                  Sobre Nós
                </span>
              </Link>
              <Link href="/nossos-eventos" onClick={scrollToTop}>
                <span className="block px-4 py-2 text-sm hover:bg-muted cursor-pointer">
                  Nossos Eventos
                </span>
              </Link>
              <Link href="/programa-fidelidade" onClick={scrollToTop}>
                <span className="block px-4 py-2 text-sm hover:bg-muted cursor-pointer">
                  Programa Fidelidade
                </span>
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden hover:bg-transparent transition-transform hover:scale-110"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="h-6 w-6 text-white hover:text-white/70" /> : <Menu className="h-6 w-6 text-white hover:text-white/70" />}
      </Button>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t absolute top-full left-0 right-0 z-50" style={{backgroundColor: '#ffffff'}}>
          <nav className="container flex flex-col py-4 space-y-3">
            <Link href="/" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
              <span 
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  isActive("/") ? "text-[#1D8649]" : "text-[#003366]"
                }`}
                style={isActive("/") ? {color: '#1D8649'} : {color: '#003366'}}
              >
                Início
              </span>
            </Link>
            
            <Link href="/contato" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
              <span 
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  isActive("/contato") ? "text-[#1D8649]" : "text-[#003366]"
                }`}
                style={isActive("/contato") ? {color: '#1D8649'} : {color: '#003366'}}
              >
                Contato
              </span>
            </Link>
            
            <Link href="/servicos" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
              <span 
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  isActive("/servicos") ? "text-[#1D8649]" : "text-[#003366]"
                }`}
                style={isActive("/servicos") ? {color: '#1D8649'} : {color: '#003366'}}
              >
                Serviços
              </span>
            </Link>
            
            <Link href="/tecnologias" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
              <span 
                className={`block py-2 text-sm font-medium cursor-pointer ${
                  isActive("/tecnologias") ? "text-[#1D8649]" : "text-[#003366]"
                }`}
                style={isActive("/tecnologias") ? {color: '#1D8649'} : {color: '#003366'}}
              >
                Tecnologias
              </span>
            </Link>
            
            <div className="border-t pt-3">
              <p className="text-sm font-semibold mb-2" style={{color: '#003366'}}>Sobre Nós</p>
              <Link href="/sobre-nos" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
                <span 
                  className={`block py-2 pl-4 text-sm cursor-pointer ${
                    isActive("/sobre-nos") ? "text-[#1D8649]" : "text-[#003366]"
                  }`}
                  style={isActive("/sobre-nos") ? {color: '#1D8649'} : {color: '#003366'}}
                >
                  Sobre Nós
                </span>
              </Link>
              <Link href="/nossos-eventos" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
                <span 
                  className={`block py-2 pl-4 text-sm cursor-pointer ${
                    isActive("/nossos-eventos") ? "text-[#1D8649]" : "text-[#003366]"
                  }`}
                  style={isActive("/nossos-eventos") ? {color: '#1D8649'} : {color: '#003366'}}
                >
                  Nossos Eventos
                </span>
              </Link>
              <Link href="/programa-fidelidade" onClick={() => { scrollToTop(); setMobileMenuOpen(false); }}>
                <span 
                  className={`block py-2 pl-4 text-sm cursor-pointer ${
                    isActive("/programa-fidelidade") ? "text-[#1D8649]" : "text-[#003366]"
                  }`}
                  style={isActive("/programa-fidelidade") ? {color: '#1D8649'} : {color: '#003366'}}
                >
                  Programa Fidelidade
                </span>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}

